import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Database, QueryConstraint, Unsubscribe } from 'firebase/database';
import {
  firebaseIsDisabled,
  isValidFirebaseDatabaseUrl,
  resolveFirebaseConfig,
  type FirebaseEnvironment,
} from '../config/firebase';

export type FirebaseAvailabilityStatus = 'idle' | 'connecting' | 'online' | 'disabled' | 'offline';

export interface FirebaseAvailability {
  readonly status: FirebaseAvailabilityStatus;
  readonly uid: string | null;
  readonly reason?: string;
}

export interface RealtimeQuery {
  readonly orderByChild?: string;
  readonly orderByKey?: boolean;
  readonly limitToLast?: number;
  readonly endBefore?: number | string;
}

export interface RemoteChild {
  readonly key: string;
  readonly value: unknown;
}

export interface CreateOnlyResult {
  readonly status: 'created' | 'exists';
  readonly existingValue?: unknown;
}

export interface RealtimeDatabasePort {
  readonly availability: FirebaseAvailability;
  connect(): Promise<FirebaseAvailability>;
  readChildren(path: string, query?: RealtimeQuery): Promise<readonly RemoteChild[]>;
  subscribeChildren(
    path: string,
    query: RealtimeQuery,
    next: (children: readonly RemoteChild[]) => void,
    error: (reason: unknown) => void,
  ): () => void;
  append(path: string, value: Readonly<Record<string, unknown>>): Promise<string>;
  createOnly(path: string, value: Readonly<Record<string, unknown>>): Promise<CreateOnlyResult>;
  readValue(path: string): Promise<unknown>;
  serverTimestamp(): unknown;
}

function cleanPath(path: string): string {
  const result = path.replace(/^\/+|\/+$/g, '');
  if (!result || result.includes('..')) throw new TypeError('Invalid Firebase path.');
  return result;
}

export class FirebaseRealtimeDatabase implements RealtimeDatabasePort {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private database: Database | null = null;
  private connectionPromise: Promise<FirebaseAvailability> | null = null;
  private state: FirebaseAvailability = Object.freeze({ status: 'idle', uid: null });

  constructor(private readonly environment: FirebaseEnvironment = import.meta.env) {}

  get availability(): FirebaseAvailability { return this.state; }

  async connect(): Promise<FirebaseAvailability> {
    if (this.state.status === 'online') return this.state;
    if (this.connectionPromise) return this.connectionPromise;
    if (firebaseIsDisabled(this.environment)) {
      this.state = Object.freeze({ status: 'disabled', uid: null, reason: 'Firebase was disabled by configuration.' });
      return this.state;
    }
    const config = resolveFirebaseConfig(this.environment);
    if (!isValidFirebaseDatabaseUrl(config.databaseURL)) {
      this.state = Object.freeze({ status: 'disabled', uid: null, reason: 'Firebase database URL is invalid.' });
      return this.state;
    }
    this.state = Object.freeze({ status: 'connecting', uid: null });
    this.connectionPromise = this.openConnection(config)
      .catch((reason: unknown) => {
        this.app = null;
        this.auth = null;
        this.database = null;
        this.state = Object.freeze({
          status: 'offline', uid: null,
          reason: reason instanceof Error ? reason.message : 'Firebase connection failed.',
        });
        return this.state;
      })
      .finally(() => { this.connectionPromise = null; });
    return this.connectionPromise;
  }

  async readChildren(path: string, request: RealtimeQuery = {}): Promise<readonly RemoteChild[]> {
    const database = await this.requireDatabase();
    const api = await import('firebase/database');
    const reference = api.ref(database, cleanPath(path));
    const constraints = this.queryConstraints(api, request);
    const snapshot = await api.get(constraints.length ? api.query(reference, ...constraints) : reference);
    const children: RemoteChild[] = [];
    snapshot.forEach((child) => {
      if (child.key !== null) children.push(Object.freeze({ key: child.key, value: child.val() as unknown }));
    });
    return children;
  }

  subscribeChildren(
    path: string,
    request: RealtimeQuery,
    next: (children: readonly RemoteChild[]) => void,
    error: (reason: unknown) => void,
  ): () => void {
    let cancelled = false;
    let unsubscribe: Unsubscribe | null = null;
    void this.requireDatabase().then(async (database) => {
      if (cancelled) return;
      const api = await import('firebase/database');
      if (cancelled) return;
      const reference = api.ref(database, cleanPath(path));
      const constraints = this.queryConstraints(api, request);
      const target = constraints.length ? api.query(reference, ...constraints) : reference;
      unsubscribe = api.onValue(target, (snapshot) => {
        const children: RemoteChild[] = [];
        snapshot.forEach((child) => {
          if (child.key !== null) children.push(Object.freeze({ key: child.key, value: child.val() as unknown }));
        });
        next(children);
      }, error);
    }).catch(error);
    return () => { cancelled = true; unsubscribe?.(); unsubscribe = null; };
  }

  async append(path: string, value: Readonly<Record<string, unknown>>): Promise<string> {
    const database = await this.requireDatabase();
    const api = await import('firebase/database');
    const child = api.push(api.ref(database, cleanPath(path)));
    if (child.key === null) throw new Error('Firebase did not allocate an entry id.');
    await api.set(child, value);
    return child.key;
  }

  async createOnly(path: string, value: Readonly<Record<string, unknown>>): Promise<CreateOnlyResult> {
    const database = await this.requireDatabase();
    const api = await import('firebase/database');
    const result = await api.runTransaction(
      api.ref(database, cleanPath(path)),
      (current: unknown) => current === null ? value : undefined,
      { applyLocally: false },
    );
    return result.committed
      ? Object.freeze({ status: 'created' })
      : Object.freeze({ status: 'exists', existingValue: result.snapshot.val() as unknown });
  }

  async readValue(path: string): Promise<unknown> {
    const database = await this.requireDatabase();
    const api = await import('firebase/database');
    const snapshot = await api.get(api.ref(database, cleanPath(path)));
    return snapshot.exists() ? snapshot.val() as unknown : null;
  }

  serverTimestamp(): unknown {
    return { '.sv': 'timestamp' };
  }

  private async openConnection(config: ReturnType<typeof resolveFirebaseConfig>): Promise<FirebaseAvailability> {
    const [appApi, authApi, databaseApi] = await Promise.all([
      import('firebase/app'), import('firebase/auth'), import('firebase/database'),
    ]);
    const appName = 'slot-yugioh-rework';
    this.app = appApi.getApps().find((candidate) => candidate.name === appName)
      ?? appApi.initializeApp(config, appName);
    this.auth = authApi.getAuth(this.app);
    this.database = databaseApi.getDatabase(this.app);
    const credential = this.auth.currentUser ? null : await authApi.signInAnonymously(this.auth);
    const uid = this.auth.currentUser?.uid ?? credential?.user.uid ?? null;
    if (!uid) throw new Error('Anonymous Firebase authentication did not return a user.');
    this.state = Object.freeze({ status: 'online', uid });
    return this.state;
  }

  private async requireDatabase(): Promise<Database> {
    const availability = await this.connect();
    if (availability.status !== 'online' || !this.database) {
      throw new Error(availability.reason ?? 'Firebase is unavailable.');
    }
    return this.database;
  }

  private queryConstraints(
    api: Readonly<{
      orderByChild(path: string): QueryConstraint;
      orderByKey(): QueryConstraint;
      endBefore(value: number | string): QueryConstraint;
      limitToLast(limit: number): QueryConstraint;
    }>,
    request: RealtimeQuery,
  ): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];
    if (request.orderByChild && request.orderByKey) throw new TypeError('Firebase query cannot use two orderings.');
    if (request.orderByChild) constraints.push(api.orderByChild(request.orderByChild));
    if (request.orderByKey) constraints.push(api.orderByKey());
    if (request.endBefore !== undefined) constraints.push(api.endBefore(request.endBefore));
    if (request.limitToLast !== undefined) {
      const limit = Math.max(1, Math.min(100, Math.trunc(request.limitToLast)));
      constraints.push(api.limitToLast(limit));
    }
    return constraints;
  }
}
